import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bell, Copy, ExternalLink, Pencil, Play, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, ErrorText, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { relTime, type Task, type TaskRun } from "@/lib/tasks";
import TaskForm from "./TaskForm";
import { StatusBadge } from "./Tasks";

interface MemoryRow {
  id: string;
  content: string;
}

export default function TaskDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const task = useQuery({ queryKey: ["tasks", id], queryFn: () => api.get<Task>(`/api/tasks/${id}`) });
  const runs = useQuery({ queryKey: ["tasks", id, "runs"], queryFn: () => api.get<TaskRun[]>(`/api/tasks/${id}/runs`) });
  const memories = useQuery({ queryKey: ["memories", id], queryFn: () => api.get<MemoryRow[]>(`/api/memories?task_id=${id}`) });
  const [editing, setEditing] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  const run = useMutation({ mutationFn: () => api.post(`/api/tasks/${id}/run`), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/tasks/${id}`),
    onSuccess: () => {
      refresh();
      navigate("/tasks");
    },
  });
  const rotate = useMutation({ mutationFn: () => api.post(`/api/tasks/${id}/webhook-secret`), onSuccess: refresh });
  const forget = useMutation({ mutationFn: (mid: string) => api.delete(`/api/memories/${mid}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["memories", id] }) });

  if (task.isLoading)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  const t = task.data;
  if (!t) return <ErrorText error={task.error} />;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 md:px-8">
        <div className="flex flex-wrap items-center gap-3 pl-10 md:pl-0">
          <Link to="/tasks" className="rounded p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" aria-label="Back">
            <ArrowLeft size={18} />
          </Link>
          <h1 className="min-w-0 flex-1 truncate text-2xl font-semibold">{t.name}</h1>
          <Button size="sm" onClick={() => run.mutate()} disabled={run.isPending}>
            <Play size={14} /> Run now
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            <Pencil size={14} /> Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => confirm(`Delete task "${t.name}"?`) && remove.mutate()} aria-label="Delete">
            <Trash2 size={14} />
          </Button>
        </div>
        <ErrorText error={run.error || remove.error} />
        <Card>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[8rem_1fr]">
            <dt className="text-zinc-500">When</dt>
            <dd>
              {t.trigger_text} {!t.enabled && <Badge tone="amber">paused</Badge>}
            </dd>
            <dt className="text-zinc-500">Next run</dt>
            <dd>{t.next_run_at ? `${new Date(t.next_run_at).toLocaleString()} (${relTime(t.next_run_at)})` : "—"}</dd>
            {t.webhook_url && (
              <>
                <dt className="text-zinc-500">Webhook</dt>
                <dd className="flex min-w-0 items-center gap-1">
                  <code className="min-w-0 truncate rounded bg-zinc-100 px-1.5 py-0.5 text-xs dark:bg-zinc-800">{t.webhook_url}</code>
                  <button className="rounded p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" onClick={() => navigator.clipboard?.writeText(t.webhook_url!)} title="Copy">
                    <Copy size={14} />
                  </button>
                  <button className="rounded p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" onClick={() => confirm("Make a new URL? The old one stops working.") && rotate.mutate()} title="New URL">
                    <RefreshCw size={14} />
                  </button>
                </dd>
              </>
            )}
          </dl>
          {t.condition && (
            <p className="mt-4 text-xs text-zinc-500">
              Only when this finds {t.condition.when === "none" ? "nothing" : "something"}: <code className="font-mono">{t.condition.sql}</code>
            </p>
          )}
          <p className="mt-4 mb-1 text-sm text-zinc-500">{t.action === "checkin" ? "Check-in question" : t.action === "notify" ? "Reminder" : "Instructions"}</p>
          <p className="text-sm whitespace-pre-wrap">{t.action === "agent" ? t.instructions : t.message}</p>
        </Card>

        <Card title="Runs">
          {runs.data?.length === 0 && <p className="text-sm text-zinc-500">No runs yet.</p>}
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {runs.data?.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2 text-sm">
                <StatusBadge status={r.status} />
                <span className="text-zinc-500">{r.trigger}</span>
                <span className="min-w-0 flex-1 truncate">
                  {new Date(r.created_at).toLocaleString()}
                  {r.error && <span className="ml-2 text-red-600">{r.error}</span>}
                </span>
                {r.notified && <Bell size={14} className="text-indigo-500" aria-label="Notified" />}
                {r.chat_id && (
                  <Link to={`/c/${r.chat_id}`} className="flex items-center gap-1 text-indigo-600 hover:underline dark:text-indigo-400">
                    Open <ExternalLink size={12} />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Card>

        {t.memory_enabled && (
          <Card title="Task memory">
            {memories.data?.length === 0 && <p className="text-sm text-zinc-500">Nothing remembered between runs yet.</p>}
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {memories.data?.map((m) => (
                <li key={m.id} className="flex items-center gap-2 py-2 text-sm">
                  <span className="min-w-0 flex-1">{m.content}</span>
                  <button className="rounded p-1 text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800" onClick={() => forget.mutate(m.id)} aria-label="Forget">
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
      {editing && <TaskForm task={t} onClose={() => setEditing(false)} />}
    </div>
  );
}
